import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/auth.service';

// ─── Estilos inline (sem dependência de biblioteca CSS) ──────────────────────
const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f2f7',
    fontFamily: "'Manrope', sans-serif",
    padding: '1rem',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 16px rgba(0,0,0,0.10)',
    width: '100%',
    maxWidth: '400px',
    overflow: 'hidden',
  },
  header: {
    backgroundColor: '#003399',
    padding: '2rem',
    textAlign: 'center',
  },
  headerTitle: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: '600',
    margin: 0,
  },
  // Logo da Alpha ERP (SVG) - exibida no cabeçalho azul
  headerLogo: {
    height: '40px',
    width: 'auto',
    maxWidth: '220px',
    objectFit: 'contain',
    display: 'block',
    margin: '0 auto',
  },
  headerSub: {
    color: 'rgba(255,255,255,0.75)',
    fontSize: '13px',
    marginTop: '4px',
  },
  body: {
    padding: '2rem',
  },
  fieldGroup: {
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: '#555',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    height: '40px',
    padding: '0 12px',
    border: '1px solid #ccc',
    borderRadius: '5px',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s',
    backgroundColor: '#fff',
    color: '#333',
  },
  inputFocus: {
    borderColor: '#003399',
    boxShadow: '0 0 0 3px rgba(0,51,153,0.10)',
  },
  passWrap: {
    position: 'relative',
  },
  passInput: {
    paddingRight: '70px',
  },
  passToggle: {
    position: 'absolute',
    right: '10px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#003399',
    fontSize: '12px',
    fontWeight: '600',
    padding: '0',
  },
  forgotLink: {
    display: 'block',
    textAlign: 'right',
    fontSize: '12px',
    color: '#003399',
    cursor: 'pointer',
    marginTop: '6px',
    textDecoration: 'none',
  },
  btnPrimary: {
    width: '100%',
    height: '42px',
    backgroundColor: '#003399',
    color: '#ffffff',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '0.5rem',
    letterSpacing: '0.3px',
    transition: 'background-color 0.2s',
  },
  btnSecondary: {
    width: '100%',
    height: '38px',
    backgroundColor: 'transparent',
    color: '#555',
    border: '1px solid #ccc',
    borderRadius: '5px',
    fontSize: '13px',
    cursor: 'pointer',
    marginTop: '0.5rem',
    transition: 'background-color 0.15s',
  },
  alertError: {
    backgroundColor: '#fff0f0',
    border: '1px solid #ffcccc',
    borderRadius: '5px',
    color: '#c0392b',
    fontSize: '13px',
    padding: '10px 14px',
    marginBottom: '1rem',
  },
  alertSuccess: {
    backgroundColor: '#f0fff4',
    border: '1px solid #a8e6c1',
    borderRadius: '5px',
    color: '#1d6a3a',
    fontSize: '13px',
    padding: '10px 14px',
    marginBottom: '1rem',
  },
  infoText: {
    fontSize: '13px',
    color: '#666',
    lineHeight: '1.6',
    marginBottom: '1.25rem',
  },
  footer: {
    borderTop: '1px solid #eee',
    padding: '0.75rem 2rem',
    textAlign: 'center',
    fontSize: '11px',
    color: '#aaa',
  },
};

// ─── Telas disponíveis ────────────────────────────────────────────────────────
const TELA = {
  LOGIN: 'login',
  ESQUECI: 'esqueci',
  REDEFINE: 'redefine',
};

// ─── Componente principal ─────────────────────────────────────────────────────
function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [tela, setTela]             = useState(TELA.LOGIN);
  const [carregando, setCarregando] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [erro, setErro]             = useState('');
  const [sucesso, setSucesso]       = useState('');

  // Campos de login
  const [email, setEmail]   = useState('');
  const [senha, setSenha]   = useState('');

  // Campos de recuperação
  const [emailRecup, setEmailRecup] = useState('');

  // Campos de redefinição
  const [novaSenha, setNovaSenha]   = useState('');
  const [confSenha, setConfSenha]   = useState('');

  const trocarTela = (proxTela) => {
    setErro('');
    setSucesso('');
    setTela(proxTela);
  };

  // ── LOGIN ──────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e?.preventDefault();
    setErro('');

    if (!email.trim()) return setErro('Informe o e-mail.');
    if (!senha)        return setErro('Informe a senha.');

    try {
      setCarregando(true);
      await login(email.trim(), senha);
      navigate('/dashboard');           // redireciona após login com sucesso
    } catch (err) {
      const msg = err.response?.data?.mensagem || 'Erro ao conectar com o servidor.';
      setErro(msg);
    } finally {
      setCarregando(false);
    }
  };

  // ── ESQUECI A SENHA ────────────────────────────────────────────────────────
  const handleEsqueci = async (e) => {
    e?.preventDefault();
    setErro('');
    setSucesso('');

    if (!emailRecup.trim()) return setErro('Informe o e-mail.');

    try {
      setCarregando(true);
      await authService.forgotPassword(emailRecup.trim());
      setSucesso('Se o e-mail existir em nossa base, você receberá as instruções em breve.');
    } catch {
      // Por segurança exibimos sempre a mesma mensagem neutra
      setSucesso('Se o e-mail existir em nossa base, você receberá as instruções em breve.');
    } finally {
      setCarregando(false);
    }
  };

  // ── REDEFINIR SENHA ────────────────────────────────────────────────────────
  const handleRedefine = async (e) => {
    e?.preventDefault();
    setErro('');
    setSucesso('');

    if (novaSenha.length < 8) return setErro('A senha deve ter no mínimo 8 caracteres.');
    if (novaSenha !== confSenha) return setErro('As senhas não conferem.');

    // O token virá pela URL quando implementarmos o e-mail: /reset-password?token=xxx
    const params = new URLSearchParams(window.location.search);
    const tokenUrl = params.get('token') || '';

    try {
      setCarregando(true);
      await authService.resetPassword(tokenUrl, novaSenha);
      setSucesso('Senha redefinida com sucesso!');
      setTimeout(() => trocarTela(TELA.LOGIN), 1500);
    } catch (err) {
      setErro(err.response?.data?.mensagem || 'Erro ao redefinir senha.');
    } finally {
      setCarregando(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.card}>

        {/* Cabeçalho azul */}
        <div style={S.header}>
          <img
            src="/alpha-logo.svg"
            alt="Alpha ERP"
            style={S.headerLogo}
          />
          <p style={S.headerSub}>Portal de Gestão</p>
        </div>

        <div style={S.body}>

          {/* ── TELA DE LOGIN ── */}
          {tela === TELA.LOGIN && (
            <form onSubmit={handleLogin} noValidate>
              {erro && <div style={S.alertError}>{erro}</div>}

              <div style={S.fieldGroup}>
                <label style={S.label} htmlFor="email">E-mail</label>
                <input
                  id="email"
                  type="email"
                  style={S.input}
                  placeholder="seu@email.com.br"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="username"
                  autoFocus
                />
              </div>

              <div style={S.fieldGroup}>
                <label style={S.label} htmlFor="senha">Senha</label>
                <div style={S.passWrap}>
                  <input
                    id="senha"
                    type={mostrarSenha ? 'text' : 'password'}
                    style={{ ...S.input, ...S.passInput }}
                    placeholder="••••••••"
                    value={senha}
                    onChange={e => setSenha(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    style={S.passToggle}
                    onClick={() => setMostrarSenha(v => !v)}
                  >
                    {mostrarSenha ? 'Ocultar' : 'Mostrar'}
                  </button>
                </div>
                <span
                  style={S.forgotLink}
                  onClick={() => trocarTela(TELA.ESQUECI)}
                >
                  Esqueci a senha
                </span>
              </div>

              <button
                type="submit"
                style={{ ...S.btnPrimary, opacity: carregando ? 0.7 : 1 }}
                disabled={carregando}
              >
                {carregando ? 'Aguarde...' : 'Entrar'}
              </button>
            </form>
          )}

          {/* ── TELA ESQUECI A SENHA ── */}
          {tela === TELA.ESQUECI && (
            <form onSubmit={handleEsqueci} noValidate>
              {erro    && <div style={S.alertError}>{erro}</div>}
              {sucesso && <div style={S.alertSuccess}>{sucesso}</div>}

              <p style={S.infoText}>
                Informe seu e-mail cadastrado. Você receberá um link para redefinir sua senha.
              </p>

              <div style={S.fieldGroup}>
                <label style={S.label} htmlFor="email-recup">E-mail</label>
                <input
                  id="email-recup"
                  type="email"
                  style={S.input}
                  placeholder="seu@email.com.br"
                  value={emailRecup}
                  onChange={e => setEmailRecup(e.target.value)}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                style={{ ...S.btnPrimary, opacity: carregando ? 0.7 : 1 }}
                disabled={carregando}
              >
                {carregando ? 'Aguarde...' : 'Enviar link de recuperação'}
              </button>

              <button
                type="button"
                style={S.btnSecondary}
                onClick={() => trocarTela(TELA.LOGIN)}
              >
                ← Voltar ao login
              </button>
            </form>
          )}

          {/* ── TELA REDEFINIR SENHA ── */}
          {tela === TELA.REDEFINE && (
            <form onSubmit={handleRedefine} noValidate>
              {erro    && <div style={S.alertError}>{erro}</div>}
              {sucesso && <div style={S.alertSuccess}>{sucesso}</div>}

              <p style={S.infoText}>
                Crie uma nova senha. Ela deve ter no mínimo 8 caracteres.
              </p>

              <div style={S.fieldGroup}>
                <label style={S.label} htmlFor="nova-senha">Nova senha</label>
                <input
                  id="nova-senha"
                  type="password"
                  style={S.input}
                  placeholder="Nova senha"
                  value={novaSenha}
                  onChange={e => setNovaSenha(e.target.value)}
                  autoFocus
                />
              </div>

              <div style={S.fieldGroup}>
                <label style={S.label} htmlFor="conf-senha">Confirmar nova senha</label>
                <input
                  id="conf-senha"
                  type="password"
                  style={S.input}
                  placeholder="Confirme a nova senha"
                  value={confSenha}
                  onChange={e => setConfSenha(e.target.value)}
                />
              </div>

              <button
                type="submit"
                style={{ ...S.btnPrimary, opacity: carregando ? 0.7 : 1 }}
                disabled={carregando}
              >
                {carregando ? 'Aguarde...' : 'Redefinir senha'}
              </button>
            </form>
          )}
        </div>

        <div style={S.footer}>Alpha ERP &copy; 2025 &mdash; Portal de Gestão v1.0</div>
      </div>
    </div>
  );
}

export default LoginPage;
